// Tag-chip voor project- en context-tags met gedeelde styling en selecteerbare varianten.
import { getTagChipColors, isContextTag } from '@/utils/colorUtils';
import { contextChipSx, taskChipSx } from '@/components/ui/taskVisualTokens';
import {
  buildTagChipListRowProjectSx,
  buildTagChipTruncateSx,
  resolveTagChipAccessibleLabel,
  tagChipDenseSx,
} from '@/components/ui/tagChipTruncate';
import {
  renderContextChip,
  renderProjectChip,
} from '@/components/ui/TagChipRenderers';

function resolveTagMeta(tag, label, projectColorIndex) {
  const isContext = isContextTag(tag) || tag.startsWith('@');
  const colorTag = isContext ? (tag.startsWith('@') ? tag : `@${tag}`) : tag;
  const colors = getTagChipColors(
    colorTag,
    isContext ? undefined : projectColorIndex,
  );
  const displayLabel = label ?? (isContext ? tag.replace(/^@/, '') : tag);
  const prefixedLabel = isContext ? `@${displayLabel}` : displayLabel;

  return { isContext, colors, prefixedLabel };
}

function buildBaseSx(
  isContext,
  dense,
  hasLabelSuffix,
  listRowProjectPill,
  projectDisplayTier,
  truncateToContainer,
) {
  const truncateSx =
    listRowProjectPill && !isContext
      ? buildTagChipListRowProjectSx(dense, projectDisplayTier)
      : buildTagChipTruncateSx(dense, hasLabelSuffix, truncateToContainer);
  return {
    ...(isContext ? contextChipSx : taskChipSx),
    ...(dense ? tagChipDenseSx : {}),
    ...truncateSx,
  };
}

/**
 * Shared tag chip: project = filled + color, context = outlined + @ prefix.
 *
 * @param {{
 *   tag: string,
 *   label?: string,
 *   dense?: boolean,
 *   selected?: boolean,
 *   selectable?: boolean,
 *   onClick?: (event: import('react').MouseEvent) => void,
 *   onDelete?: () => void,
 *   'data-testid'?: string,
 *   'aria-label'?: string,
 *   labelSuffix?: string,
 *   tabIndex?: number,
 *   projectColorIndex?: number,
 *   listRowProjectPill?: boolean,
 *   projectDisplayTier?: 'full' | 'abbrev' | 'dot',
 *   measureOnly?: boolean,
 *   truncateToContainer?: boolean,
 * }} props
 */
export default function TagChip({
  tag,
  label,
  projectColorIndex,
  dense = false,
  selected = false,
  selectable = false,
  onClick,
  onDelete,
  'data-testid': testId,
  'aria-label': ariaLabel,
  labelSuffix,
  tabIndex,
  listRowProjectPill = false,
  projectDisplayTier = 'full',
  measureOnly = false,
  truncateToContainer = false,
}) {
  const { isContext, colors, prefixedLabel } = resolveTagMeta(
    tag,
    label,
    projectColorIndex,
  );
  const hasLabelSuffix = Boolean(labelSuffix);
  const accessibleLabel = resolveTagChipAccessibleLabel(
    prefixedLabel,
    ariaLabel,
    labelSuffix,
  );
  const baseSx = buildBaseSx(
    isContext,
    dense,
    hasLabelSuffix,
    listRowProjectPill,
    projectDisplayTier,
    truncateToContainer,
  );
  const chipProps = {
    prefixedLabel,
    colors,
    selected,
    baseSx,
    onClick,
    onDelete,
    testId,
    tabIndex,
    labelSuffix,
    dense,
    accessibleLabel,
    projectDisplayTier,
    measureOnly,
  };

  if (isContext) {
    return renderContextChip(chipProps);
  }

  return renderProjectChip({ ...chipProps, selectable });
}
